import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import { Command as CommandPrimitive } from "cmdk";
import type * as React from "react";

import {
  DialogContent,
  type DialogContentProps,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// Variant definitions
const commandInputVariants = cva(
  [
    // Layout & sizing
    "flex h-10 w-full",
    // Appearance
    "rounded-md bg-transparent",
    // Spacing
    "py-3",
    // Typography
    "font-normal tracking-[0.12px]",
    // Placeholder styles
    "placeholder:text-muted-foreground",
    // Focus styles
    "outline-hidden",
    // Disabled states
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const commandGroupVariants = cva(
  [
    // Layout
    "overflow-hidden p-1",
    // Base text color
    "text-foreground",
    // Group heading base styles
    "**:[[cmdk-group-heading]]:text-gray-600",
    "**:[[cmdk-group-heading]]:px-2",
    "**:[[cmdk-group-heading]]:py-1.5",
    "**:[[cmdk-group-heading]]:font-normal",
    "**:[[cmdk-group-heading]]:tracking-[0.12px]",
  ],
  {
    variants: {
      size: {
        sm: "**:[[cmdk-group-heading]]:text-xs",
        md: "**:[[cmdk-group-heading]]:text-sm",
        lg: "**:[[cmdk-group-heading]]:text-base",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const commandItemVariants = cva(
  [
    // Layout & interaction
    "relative flex cursor-pointer gap-2 rounded-xs px-2 py-1.5 select-none w-full",
    // Typography
    "font-normal tracking-[0.12px] text-gray-700",
    // Focus & selection states
    "outline-hidden",
    "data-[selected=true]:bg-gray-100 data-[selected=true]:text-slate-900 data-[selected=true]:font-medium",
    "aria-selected:bg-gray-100 aria-selected:text-slate-900 aria-selected:font-medium",
    // Disabled states
    "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
    // Icon styles
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "[&_svg:not([class*='size-'])]:size-4",
    "[&_svg:not([class*='text-'])]:text-muted-foreground",
  ],
  {
    variants: {
      size: {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
      },
      textBehavior: {
        normal: "items-center",
        wrap: "flex-wrap items-start min-h-fit [&>span]:whitespace-normal [&>span]:wrap-break-word",
        truncate:
          "items-center [&>span]:truncate [&>span]:block [&>span]:min-w-0",
      },
    },
    defaultVariants: {
      size: "sm",
      textBehavior: "truncate",
    },
  },
);

// Base command styles
const commandDialogStyles = [
  "[&_[cmdk-group-heading]]:text-muted-foreground",
  "**:data-[slot=command-input-wrapper]:h-12",
  "**:[[cmdk-group-heading]]:px-2",
  "**:[[cmdk-group-heading]]:font-medium",
  "**:[[cmdk-group]]:px-2",
  "[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0",
  "[&_[cmdk-input-wrapper]_svg]:h-5",
  "[&_[cmdk-input-wrapper]_svg]:w-5",
  "**:[[cmdk-input]]:h-12",
  "**:[[cmdk-item]]:px-2",
  "**:[[cmdk-item]]:py-3",
  "[&_[cmdk-item]_svg]:h-5",
  "[&_[cmdk-item]_svg]:w-5",
].join(" ");

// Type definitions
interface CommandDialogProps extends Omit<DialogContentProps, "children"> {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  shouldFilter?: boolean;
}

type CommandInputProps = React.ComponentProps<typeof CommandPrimitive.Input> &
  VariantProps<typeof commandInputVariants> & {
    icon?: React.ReactNode;
    wrapperClassName?: string;
  };

type CommandGroupProps = React.ComponentProps<typeof CommandPrimitive.Group> &
  VariantProps<typeof commandGroupVariants>;

type CommandItemProps = React.ComponentProps<typeof CommandPrimitive.Item> &
  VariantProps<typeof commandItemVariants>;

// Components
const Command = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) => (
  <CommandPrimitive
    data-slot="command"
    className={cn(
      "bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
      className,
    )}
    {...props}
  />
);

const CommandDialog = ({
  title = "Command Palette",
  description,
  children,
  className,
  open,
  onOpenChange,
  closeButton = true,
  shouldFilter,
  ...props
}: CommandDialogProps) => {
  if (!open) return null;

  return (
    <DialogOverlay
      isOpen={open}
      onOpenChange={onOpenChange}
      isDismissable={true}
    >
      <DialogContent
        className={cn("overflow-hidden p-0 max-w-2xl", className)}
        closeButton={closeButton}
        onClose={() => onOpenChange?.(false)}
        aria-label={title}
        {...props}
      >
        {title && (
          <DialogHeader className="sr-only">
            <DialogTitle>{title}</DialogTitle>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </DialogHeader>
        )}
        <Command shouldFilter={shouldFilter} className={commandDialogStyles}>
          {children}
        </Command>
      </DialogContent>
    </DialogOverlay>
  );
};

const CommandInput = ({
  className,
  size,
  icon = <MagnifyingGlassIcon className="size-4 shrink-0 opacity-50" />,
  wrapperClassName,
  ...props
}: CommandInputProps) => (
  <div
    data-slot="command-input-wrapper"
    className={cn(
      "flex h-9 items-center gap-2",
      "border-t border-x border-b",
      "border-t-transparent border-x-transparent border-b-border",
      "rounded-t-md px-3",
      "focus-within:border focus-within:border-blue-600 focus-within:border-solid",
      "transition-colors",
      wrapperClassName,
    )}
  >
    {icon}
    <CommandPrimitive.Input
      data-slot="command-input"
      className={cn(commandInputVariants({ size }), className)}
      {...props}
    />
  </div>
);

const CommandList = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) => (
  <CommandPrimitive.List
    data-slot="command-list"
    className={cn(
      "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
      className,
    )}
    {...props}
  />
);

const CommandEmpty = ({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) => (
  <CommandPrimitive.Empty
    data-slot="command-empty"
    className="py-6 text-center text-sm"
    {...props}
  />
);

const CommandGroup = ({ className, size, ...props }: CommandGroupProps) => (
  <CommandPrimitive.Group
    data-slot="command-group"
    className={cn(commandGroupVariants({ size }), className)}
    {...props}
  />
);

const CommandSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) => (
  <CommandPrimitive.Separator
    data-slot="command-separator"
    className={cn("bg-border -mx-1 h-px", className)}
    {...props}
  />
);

const CommandItem = ({
  className,
  size,
  textBehavior,
  ...props
}: CommandItemProps) => (
  <CommandPrimitive.Item
    data-slot="command-item"
    className={cn(commandItemVariants({ size, textBehavior }), className)}
    {...props}
  />
);

const CommandShortcut = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    data-slot="command-shortcut"
    className={cn(
      "text-muted-foreground ml-auto text-xs tracking-widest",
      className,
    )}
    {...props}
  />
);

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
